import { useState, ReactNode } from 'react';
import { Send, Mic, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { GibletEngageMenu, EngageAction } from './GibletEngageMenu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface GibletInputAreaProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onEngageAction: (action: EngageAction) => void;
  isLoading: boolean;
  connectedKBsCount: number;
  maxKBs: number;
  variant?: 'panel' | 'bar';
  extraButtons?: ReactNode;
  leftExtraButtons?: ReactNode;
}

export const GibletInputArea = ({
  value,
  onChange,
  onSend,
  onEngageAction,
  isLoading,
  connectedKBsCount,
  maxKBs,
  variant = 'panel',
  extraButtons,
  leftExtraButtons,
}: GibletInputAreaProps) => {
  const [mode, setMode] = useState<'chat' | 'voice'>('chat');

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !isLoading) {
        onSend();
      }
    }
  };

  const handleVoiceClick = () => {
    toast.info('Voice mode coming soon', {
      description: 'Speech-to-text functionality will be available in a future update.',
    });
  };

  const isReady = value.trim() && !isLoading;

  // Bar variant: single-line horizontal layout
  if (variant === 'bar') {
    return (
      <div className="flex items-center gap-1.5 bg-muted/30 rounded-full border border-border/40 px-2 py-1.5 focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/20 transition-all w-full">
        {/* Workflow Zap button */}
        <GibletEngageMenu
          onAction={onEngageAction}
          connectedKBsCount={connectedKBsCount}
          maxKBs={maxKBs}
        />

        {/* Left extra buttons (e.g. Marketplace) */}
        {leftExtraButtons}

        {/* Single-line input */}
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message Giblet..."
          className="flex-1 border-0 bg-transparent h-8 text-sm focus-visible:ring-0 placeholder:text-muted-foreground/60"
          disabled={isLoading}
        />

        {/* Extra buttons (Marketplace, Notifications) */}
        {extraButtons}

        {/* Chat / Voice toggles */}
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-6 w-6 rounded",
                    mode === 'chat' && "bg-primary/10 text-primary"
                  )}
                  onClick={() => setMode('chat')}
                >
                  <MessageSquare className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Text chat</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-6 w-6 rounded",
                    mode === 'voice' && "bg-primary/10 text-primary"
                  )}
                  onClick={handleVoiceClick}
                >
                  <Mic className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Voice mode (coming soon)</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>

        {/* Send */}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                className={cn(
                  "h-8 w-8 rounded-full flex-shrink-0 transition-colors",
                  isReady && "bg-primary hover:bg-primary/90",
                  !isReady && "bg-muted text-muted-foreground hover:bg-muted"
                )}
                onClick={() => onSend()}
                disabled={!value.trim() || isLoading}
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Send message</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  // Panel variant: original multi-line layout
  return (
    <div className="border-t bg-background p-3">
      {/* Single soft-edged container */}
      <div className="bg-muted/30 rounded-2xl border border-border/40 overflow-hidden focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/20 transition-all">
        {/* Textarea section - top */}
        <div className="px-4 pt-3 pb-2">
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Message Giblet..."
            className="min-h-[72px] max-h-[200px] resize-none border-0 bg-transparent p-0 focus-visible:ring-0 text-sm placeholder:text-muted-foreground/60"
            rows={3}
            disabled={isLoading}
          />
        </div>

        {/* Buttons row - bottom */}
        <div className="px-3 py-2 flex items-center justify-between border-t border-border/20">
          {/* Left side: Workflow menu */}
          <div className="flex items-center gap-1.5">
            <GibletEngageMenu
              onAction={onEngageAction}
              connectedKBsCount={connectedKBsCount}
              maxKBs={maxKBs}
            />
          </div>

          {/* Right side: Chat, Voice, Send */}
          <div className="flex items-center gap-1.5">
            <TooltipProvider delayDuration={300}>
              <div className="flex items-center gap-0.5">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "h-6 w-6 rounded",
                        mode === 'chat' && "bg-primary/10 text-primary"
                      )}
                      onClick={() => setMode('chat')}
                    >
                      <MessageSquare className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Text chat</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "h-6 w-6 rounded",
                        mode === 'voice' && "bg-primary/10 text-primary"
                      )}
                      onClick={handleVoiceClick}
                    >
                      <Mic className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Voice mode (coming soon)</TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>

            {/* Send Button */}
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    className={cn(
                      "h-8 w-8 rounded-full flex-shrink-0 transition-colors",
                      isReady && "bg-primary hover:bg-primary/90",
                      !isReady && "bg-muted text-muted-foreground hover:bg-muted"
                    )}
                    onClick={() => onSend()}
                    disabled={!value.trim() || isLoading}
                  >
                    <Send className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Send message</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </div>
  );
};
